import React from 'react';
import {
  useForm,
  SubmitHandler,
  FormProvider,
} from 'react-hook-form';
import { Box, Grid, GridItem } from '@chakra-ui/react';

import { InputControl } from '../../ui-elements/InputControl';
import { FormInputsType, PaymentElementResponse } from '../../../@types';
import { sanitizeErrors, unpackErrorMessage } from '../../../utils/errors';
import { useTranslations } from '../../../translations/useTranslations';
import { useCommonOptionsStore } from '../../../store';

import { OxxoFormProps } from './oxxo.types';
import { OxxoFormPropTypes } from './oxxo.prop-types';

const OxxoForm = React.forwardRef(({
  onChange,
  onLoadStart,
  onLoadEnd,
  onSuccess,
  onError,
  onSubmit,
  guest,
}: OxxoFormProps, forwardRef: any) => {
  const lang = useCommonOptionsStore(state => state.lang);
  const [t] = useTranslations(lang);

  const formMethods = useForm<FormInputsType>({
    mode: 'onChange',
    defaultValues: {
      name: `${guest?.firstName} ${guest?.lastName}`.trim(),
      email: guest?.email || '',
    },
  });

  const { handleSubmit, formState, getValues, trigger } = formMethods;

  const {
    errors,
    isLoading,
    isSubmitSuccessful,
    isSubmitting,
  } = formState;

  const errCount = React.useMemo(() => (errors ? Object.keys(errors).length : 0), [errors]);

  React.useEffect(() => {
    if (onLoadStart && isLoading) onLoadStart();
    if (onLoadEnd && isLoading) onLoadEnd();
  }, [isLoading, onLoadStart, onLoadEnd]);

  React.useEffect(() => {
    if (onLoadStart && isSubmitting) onLoadStart();
    if (onLoadEnd && isSubmitting) onLoadEnd();
  }, [isSubmitting, onLoadStart, onLoadEnd]);

  React.useEffect(() => {
    if (onSuccess && isSubmitSuccessful) onSuccess();
  }, [isSubmitSuccessful, onSuccess]);

  React.useEffect(() => {
    if (onError && errCount) onError(sanitizeErrors(errors));
  }, [onError, errors, errCount]);

  const onFieldChange = (fieldName: string) => {
    if (onChange) onChange({ [fieldName]: getValues(fieldName) });
    if (onError && errors[fieldName]) onError({ [fieldName]: errors[fieldName]?.message });
  };

  const layoutProps: Record<string, any> = {
    gap: 6,
    columnGap: 4,
  };

  const submitHandler: SubmitHandler<FormInputsType> = async (formData: any) => {
    const data = { ...formData };

    if (onSubmit) {
      const responseData: PaymentElementResponse = {
        ...data,
        paymentMethod: 'oxxo',
      };

      onSubmit({ ...responseData });
    }
  };

  const submitErrorHandler: SubmitHandler<FormInputsType> = async () => {
    if (onError) onError(sanitizeErrors(errors));
  };

  React.useImperativeHandle(forwardRef, () => ({
    submit() {
      handleSubmit(submitHandler, submitErrorHandler)();
    },
    async validate() {
      return trigger();
    },
  }));

  return (
    <>
      <FormProvider {...formMethods}>
        <div>

          <Box
            paddingTop={(layoutProps as any).gap}
            paddingBottom={(layoutProps as any).gap}
          >{t('oxxoMessage')}</Box>

          <Grid
            templateColumns="repeat(2, 1fr)"
            templateRows="repeat(auto-fill, 1fr)"
            gap={(layoutProps as any).gap}
            columnGap={(layoutProps as any).columnGap}
          >
            <GridItem>
              <InputControl
                name="name"
                label={t('fullName')}
                error={unpackErrorMessage(errors, 'name')}
                onChange={onFieldChange}
                isRequired
              />
            </GridItem>
            <GridItem>
              <InputControl
                name="email"
                label={t('email')}
                error={unpackErrorMessage(errors, 'email')}
                onChange={onFieldChange}
                isRequired
              />
            </GridItem>
          </Grid>
        </div>
      </FormProvider>
    </>
  );
});

OxxoForm.propTypes = OxxoFormPropTypes;

export { OxxoForm };
